import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  UrlTree,
} from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { PublicClientApplication, RedirectRequest } from '@azure/msal-browser';
import { protectedResources } from '@core/auth-config';
import { RoutePath } from '@core/routing/routing.types';
import { UserService } from '../oauth-service/user.service';
import { MenuService } from './menu.service';

function getFullPath(route: ActivatedRouteSnapshot): string {
  const segments = [];

  while (route) {
    if (route.routeConfig && route.routeConfig.path) {
      segments.unshift(route.routeConfig.path);
    }
    route = route.parent!;
  }

  return '/' + segments.join('/');
}

function checkRoles(route: ActivatedRouteSnapshot): boolean | UrlTree {
  const router = inject(Router);
  const current = getFullPath(route);
  const menuService = inject(MenuService);
  const userService = inject(UserService);
  const menuRoutesRoles = menuService.routesRoles;
  const currentUserRoles = userService.getCurrentUserRoles();
  const foundRoute = menuRoutesRoles.find(x => current == x.route);

  // Case 1: no route is found -> allowed to see all
  if (!foundRoute) {
    return true;
  }

  // Case 2: not loggedIn User -> navigate to NONE
  if (!currentUserRoles) {
    return router.parseUrl(RoutePath.DASHBOARD);
  }

  // Case 3: loggedIn User with Roles -> compare user roles with route roles
  let isAllowed = false;
  isAllowed = foundRoute.roles.some(role => currentUserRoles.includes(role));
  return isAllowed ? true : router.parseUrl(RoutePath.NO_ACCESS);
}

export const RolesAuthGuard: CanActivateFn = (route, _state) => {
  const msal = inject(MsalService);

  const account = msal.instance.getActiveAccount();
  if (account && account.idTokenClaims?.exp * 1000 > Date.now()) {
    // Access Token is still valid
    return checkRoles(route);
  } else {
    // Access Token is not valid, redirect to login
    const msalInstance: PublicClientApplication =
      msal.instance as PublicClientApplication;
    msalInstance.clearCache();
    const loginRequest: RedirectRequest = {
      scopes: protectedResources.getIncident.scopes,
      prompt: 'select_account',
    };

    msalInstance.initialize();
    msalInstance
      .handleRedirectPromise()
      .then(async () => {
        await msalInstance.loginRedirect(loginRequest);
      })
      .catch(err => {
        console.log(err);
      });
    return false;
  }
};
